import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { CardSkeleton, TableSkeleton } from "@/components/Loading";
import { ErrorAlert } from "@/components/ErrorAlert";
import { FileSpreadsheet, PencilLine } from "lucide-react";
import { Card, CardDescription, CardHeader } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader } from "@/components/PageHeader";
import { UserAvatar } from "@/components/UserAvatar";
import { VersionTimeline } from "@/components/VersionTimeline";
import { useLocationSlot } from "@/lib/location";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Pager } from "@/components/Pager";
import { ManualEntryDialog, type ManualNotice, type ManualTarget } from "@/components/ManualEntryDialog";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/StatusBadge";
import type { Role, UploadDetailResponse, UploadRecordItem, UploadRecordsResponse } from "../../shared/api";
import { SOURCE_TYPES, type SourceType } from "../../shared/rows";
import { formatNumber, parseRecordKey } from "../../shared/versions";
import { sourceTypeText } from "../../shared/sources";
import { versionLabel } from "../../shared/upload";
import { formatPeriod } from "../../shared/report";
import { ApiFailure, errorText, getUpload, getUploadRecords } from "@/lib/client";
import { SOURCES, navigate, replaceQuery, uploadHref } from "@/lib/nav";
import { canWrite } from "@/lib/guard";
import { PRODUCT } from "@/lib/product";
import { displayValue, fieldLabel, formatDay } from "@/lib/upload";

export { SOURCES, uploadHref };

const LINK = "text-primary underline-offset-4 hover:underline outline-none focus-visible:ring-[3px] focus-visible:ring-ring rounded-sm";

/** Dosya hücresi: dosya adı, elle girişte "Elle giriş" (numara başlıkta/ilk kolonda var). */
const fileText = (u: { origin: "file" | "manual"; fileName: string | null }) => (u.origin === "manual" ? "Elle giriş" : u.fileName ?? "");

/** Yükleme bilgisi (ui.md §12.11): iki sütunlu terim-değer ızgarası, yükleyen avatarlı. "Yükleme no" yazılmaz (h1'de var). */
export function UploadInfo({ u }: { u: UploadDetailResponse }) {
  const rows: [string, ReactNode, string?][] = [
    ["Dosya", fileText(u)], ["Kaynak tipi", sourceTypeText(u.sourceType)], ["Zaman", formatDay(u.createdAt)],
    ["Yükleyen", <span className="inline-flex items-center gap-2"><UserAvatar name={u.uploadedBy} />{u.uploadedBy}</span>],
    ["Profil", u.profileName ?? "—"], ["Kayıt sayısı", formatNumber(u.rowCount), "tabular-nums"],
    ...(u.origin === "manual" ? [] : [["Dönem", u.periodStart && u.periodEnd ? formatPeriod(u.periodStart, u.periodEnd) : "Tam hâl"] as [string, ReactNode]]),
    ...(u.periodStart && u.periodEnd ? [["Versiyondaki kayıt", <>{formatNumber(u.versionRecordCount)}<p className="text-xs text-muted-foreground">Bu dosyanın kayıtları ve önceki versiyondan taşınanlar.</p></>, "tabular-nums"] as [string, ReactNode, string]] : []), [u.origin === "manual" ? "Parmak izi" : "Dosya parmak izi", u.fileHash.slice(0, 8), "font-mono"],
    ...(u.justification ? [["Gerekçe", u.justification] as [string, string]] : []),
  ];
  return (
    <Card role="region" aria-labelledby="info-h" className="gap-0 overflow-clip p-0 text-sm">
      <CardHeader className="grid-rows-[auto] gap-1 border-b px-4 py-3 [.border-b]:pb-3"><h2 id="info-h" className="text-base font-semibold">Yükleme bilgisi</h2></CardHeader>
      <dl className="grid gap-x-6 gap-y-3 p-4 sm:grid-cols-2">
        {rows.map(([k, v, cls]) => <div key={k} className="min-w-0"><dt className="text-xs text-muted-foreground">{k}</dt><dd className={`mt-0.5 break-words ${cls ?? ""}`}>{v}</dd></div>)}
      </dl>
    </Card>
  );
}

/** Kaynak tipinin bütün yüklemeleri, yeniden eskiye. Açık olan işaretli (metin + aria-current), diğerleri gerçek adresli bağlantı. Başlık kartta sabit, zaman çizgisi kendi kutusunda kayar. */
export function Versions({ u }: { u: UploadDetailResponse }) {
  return (
    <Card role="region" aria-labelledby="versions-h" className="min-w-0 gap-0 overflow-clip p-0">
      <CardHeader className="grid-rows-[auto] gap-1 border-b px-4 py-3 [.border-b]:pb-3">
        <h2 id="versions-h" className="text-base font-semibold">Versiyonlar</h2>
        <CardDescription className="text-xs">Bu kaynak tipi için yüklenen bütün dosyalar, yeniden eskiye.</CardDescription>
      </CardHeader>
      <div className="max-h-72 overflow-auto p-4">
        <VersionTimeline items={u.versions.map((v) => {
          const here = v.uploadId === u.uploadId, Icon = v.origin === "manual" ? PencilLine : FileSpreadsheet;
          return {
            id: v.uploadId, active: here,
            children: (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                <Icon aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
                {here ? <span className="font-medium">{`#${v.uploadId} · bu yükleme`}</span> : <a href={uploadHref(v.uploadId)} className={LINK} onClick={(e) => navigate(e, uploadHref(v.uploadId))}>#{v.uploadId}</a>}
                <span>{fileText(v)}</span>
                <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"><UserAvatar name={v.uploadedBy} />{v.uploadedBy}</span>
                <span className="tabular-nums text-muted-foreground">{formatDay(v.createdAt)} · {formatNumber(v.rowCount)} kayıt</span>
                <span className="font-mono text-xs text-muted-foreground">{v.fileHash.slice(0, 8)}</span>
              </div>
            ),
          };
        })} />
      </div>
    </Card>
  );
}

/** Excel satırı hücresi: taşınan kayıt iki satır, sayı üstte, altında "Yükleme #9" bağlantısı (ADR-0018); elle girişte "—". */
export function RowCell({ r, uploadId }: { r: UploadRecordItem; uploadId: number }) {
  if (r.rowNo === null) return <>—</>;
  const n = formatNumber(r.rowNo), src = r.sourceUploadId;
  if (src === null || src === uploadId) return <>{n}</>;
  return <>{n}<span className="sr-only"> · </span><a href={uploadHref(src)} className={`${LINK} block text-xs font-normal`} onClick={(e) => navigate(e, uploadHref(src))}>{versionLabel("file", src)}</a></>;
}

/** Kayıtlar tablosunun kolonları (spec §6 E2): kaynak tipinin kimlik alanları, sonra kimlikte olmayan eşlenebilir alanlar. */
export function recordColumns(sourceType: string): { key: string[]; fields: string[]; kinds: Record<string, string> } {
  if (!(sourceType in SOURCE_TYPES)) return { key: [], fields: [], kinds: {} };
  const t = SOURCE_TYPES[sourceType as SourceType];
  return { key: [...t.key], fields: [...t.required, ...t.optional].filter((f) => !(t.key as readonly string[]).includes(f)), kinds: t.kinds as Record<string, string> };
}
/** Kimlik hücresinin hedefi: gerçek adres, Ctrl/Cmd+tık yeni sekme açar (E6 `?sourceType=&recordKey=` okur). */
export const historyHref = (sourceType: string, recordKey: string) => `/history?sourceType=${sourceType}&recordKey=${encodeURIComponent(recordKey)}`;
/** Ekran okuyucu tam kimliği ve hedefi duyar: "IM-2026/2104 kalem no 1: Veri Geçmişi'nde aç". */
export function recordLinkLabel(sourceType: string, recordKey: string): string {
  return `${recordIdText(sourceType, recordKey)}: Veri Geçmişi'nde aç`;
}
/** Kimliğin okunuşu: "IM-2026/2104 kalem no 1". */
export function recordIdText(sourceType: string, recordKey: string): string {
  const parts = parseRecordKey(recordKey), { key } = recordColumns(sourceType);
  return parts.map((p, i) => (i === 0 ? p : `${fieldLabel(key[i] ?? "").toLocaleLowerCase("tr-TR")} ${p}`)).join(" ");
}

// Tablo border-collapse olduğundan çizgiler kayar: sabit hücrelerde border yerine inset gölge (designer, #197). Gövde hücresi hover'da opak zemin alır.
const STICKY = "sticky bg-card shadow-[inset_-1px_0_0_var(--border)]";
const BODY_STICKY = `${STICKY} z-10 [tr:hover>&]:bg-[color-mix(in_oklab,var(--muted)_50%,var(--card))]`;
const HEAD_BG = "bg-[color-mix(in_oklab,var(--muted)_60%,var(--card))]"; // opak: kayarken satırlar altından görünmez
const HEAD_LINE = "shadow-[inset_0_-1px_0_var(--border)]";
const BODY_STICKY_END = "sticky right-0 z-10 bg-card shadow-[inset_1px_0_0_var(--border)] [tr:hover>&]:bg-[color-mix(in_oklab,var(--muted)_50%,var(--card))]";
const HEAD_STICKY_END = "sticky top-0 right-0 z-20 bg-[color-mix(in_oklab,var(--muted)_60%,var(--card))] shadow-[inset_1px_0_0_var(--border),inset_0_-1px_0_var(--border)]";
const HEAD_STICKY_LINE = "shadow-[inset_-1px_0_0_var(--border),inset_0_-1px_0_var(--border)]";

/** Adresteki arama ve sayfa (`?keyContains=&page=`); bozuk sayfa 1 sayılır. */
function initialQuery(): { page: number; query: string } {
  const q = new URLSearchParams(window.location.search), p = Number(q.get("page"));
  return { page: Number.isInteger(p) && p > 0 ? p : 1, query: q.get("keyContains") ?? "" };
}

/** "Kayıtlar" bölümü: sayfalı, salt okunur (spec §6 E2). Arama ve sayfa adreste tutulur, `replace` ile yazılır. */
export function UploadRecords({ uploadId, sourceType, actions, note }: {
  uploadId: number; sourceType: string;
  /** Yazma yetkisi olana, son versiyonda (ui.md §11.1): "Yeni kayıt" düğmesi ve satırda "Düzelt". */
  actions?: { onNew: (opener: HTMLElement) => void; onEdit: (r: UploadRecordItem, opener: HTMLElement) => void };
  /** Kayıtlar başlığının altındaki cümle (eski versiyonda eylemlerin neden olmadığı). */
  note?: ReactNode;
}) {
  const init = useRef(initialQuery()).current;
  const [data, setData] = useState<UploadRecordsResponse | null>(null);
  const [error, setError] = useState("");
  const [page, setPage] = useState(init.page);
  const [text, setText] = useState(init.query);
  const [query, setQuery] = useState(init.query);
  const [fetching, setFetching] = useState(true);
  const [retry, setRetry] = useState(0); // hatadan sonra aynı sayfaya yeniden basınca istek yeniden atılır
  const box = useRef<HTMLDivElement>(null);
  const { key, fields, kinds } = recordColumns(sourceType);
  const cols = [...key, ...fields];

  useEffect(() => {
    let live = true;
    setFetching(true); setError("");
    getUploadRecords(uploadId, { page, keyContains: query }).then((r) => { if (live) { setData(r); box.current?.scrollTo?.({ top: 0 }); } }, (e) => { if (live) setError(errorText(e)); }).finally(() => { if (live) setFetching(false); });
    return () => { live = false; };
  }, [uploadId, page, query, retry]);

  const to = (p: number, q: string) => { setPage(p); setQuery(q); replaceQuery("keyContains", q || null); replaceQuery("page", p > 1 ? p : null); };
  const search = (e: FormEvent) => { e.preventDefault(); to(1, text.trim()); };
  const clear = () => { setText(""); to(1, ""); };
  const right = (f: string) => kinds[f] === "number" ? "text-right tabular-nums" : "";
  const empty = data?.total === 0 && !query;

  const row = (r: UploadRecordItem) => {
    const parts = parseRecordKey(r.recordKey), href = historyHref(sourceType, r.recordKey);
    return (
      <TableRow key={r.recordKey}>
        <TableCell className={`${BODY_STICKY} left-0 w-24 min-w-24 max-w-24 whitespace-normal text-right tabular-nums`}><RowCell r={r} uploadId={uploadId} /></TableCell>
        {cols.map((f, i) => i === 0 ? (
          <TableCell key={f} className={`${BODY_STICKY} left-24 font-mono`}>
            <a href={href} aria-label={recordLinkLabel(sourceType, r.recordKey)} className={LINK} onClick={(e) => navigate(e, href)}>{parts[0]}</a>
            {r.manualEntryId !== null && <StatusBadge tone="info" className="ml-2 font-sans">Elle girildi</StatusBadge>}
          </TableCell>
        ) : (
          <TableCell key={f} className={`${right(f)} ${i < key.length ? "font-mono" : ""}`}>{displayValue(sourceType, f, r.fields[f] ?? (i < key.length ? parts[i] ?? null : null))}</TableCell>
        ))}
        {actions && (
          <TableCell className={BODY_STICKY_END}>
            <Button type="button" variant="outline" size="xs" className="-my-0.5" aria-label={`${recordIdText(sourceType, r.recordKey)} kaydını düzelt`} onClick={(e) => actions.onEdit(r, e.currentTarget)}>Düzelt</Button>
          </TableCell>
        )}
      </TableRow>
    );
  };
  const width = cols.length + 1 + (actions ? 1 : 0);

  return (
    <Card role="region" aria-labelledby="records-h" className="min-w-0 gap-0 overflow-clip p-0">
      <CardHeader className="flex flex-wrap items-end justify-between gap-4 border-b px-4 py-3 [.border-b]:pb-3">
        <div>
          <div className="flex items-center gap-4">
            <h2 id="records-h" className="text-base font-semibold">Kayıtlar</h2>
            {actions && <Button type="button" variant="outline" size="sm" onClick={(e) => actions.onNew(e.currentTarget)}>Yeni kayıt</Button>}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">Kimliğe tıklayınca kaydın Veri Geçmişi açılır.</p>
          {note && <p className="text-sm text-muted-foreground">{note}</p>}
        </div>
        {data && !empty && (
          <form role="search" onSubmit={search} className="flex flex-wrap items-end gap-2">
            <div className="flex flex-col gap-2">
              <Label htmlFor="records-q">Kayıt kimliğinde ara</Label>
              <Input id="records-q" className="w-64" value={text} onChange={(e) => setText(e.target.value)} maxLength={200} />
            </div>
            <Button type="submit" variant="outline" size="sm">Ara</Button>
            {query && <Button type="button" variant="outline" size="sm" onClick={clear}>Temizle</Button>}
          </form>
        )}
      </CardHeader>
      {error && <div className="p-4"><ErrorAlert>{error}</ErrorAlert></div>}
      {!data && !error && <div className="p-4"><TableSkeleton cols={width} /></div>}
      {empty && <p className="p-4 text-sm text-muted-foreground">Bu yüklemede kayıt yok.</p>}
      {data && !empty && (
        <>
          <div ref={box} aria-busy={fetching} className="max-h-[calc(100svh-10rem)] min-h-64 overflow-auto transition-opacity aria-busy:opacity-50">
            <Table containerClassName="overflow-visible">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className={`sticky top-0 left-0 z-20 w-24 min-w-24 max-w-24 ${HEAD_BG} text-right text-xs font-medium tabular-nums text-muted-foreground ${HEAD_STICKY_LINE}`}>Excel satırı</TableHead>
                  {cols.map((f, i) => <TableHead key={f} className={`sticky top-0 ${HEAD_BG} text-xs font-medium text-muted-foreground ${i === 0 ? `left-24 z-20 ${HEAD_STICKY_LINE}` : `z-10 ${HEAD_LINE}`} ${right(f)}`}>{fieldLabel(f)}</TableHead>)}
                  {actions && <TableHead className={`${HEAD_STICKY_END} text-xs font-medium text-muted-foreground`}>İşlem</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.length === 0
                  ? <TableRow><TableCell colSpan={width} className="text-muted-foreground">Bu aramayla kayıt bulunamadı.</TableCell></TableRow>
                  : data.items.map(row)}
              </TableBody>
            </Table>
          </div>
          <div className="border-t px-4 py-2"><Pager page={data.page} total={data.total} pageSize={data.pageSize} onPage={(p) => { to(p, query); setRetry((n) => n + 1); }} label="Kayıtlar sayfalama" showTotal /></div>
        </>
      )}
    </Card>
  );
}

const BackLink = ({ children = "Veri Kaynakları" }: { children?: string }) => <a href={SOURCES} className={LINK} onClick={(e) => navigate(e, SOURCES)}>{children}</a>;

/** Başarıdan gelen sonuç satırı (`history.state`); bozuk ya da yabancı durum yok sayılır. */
function readNotice(): ManualNotice | null {
  if (typeof window === "undefined") return null;
  const n = (window.history.state as { manualEntry?: Partial<ManualNotice> } | null)?.manualEntry;
  return n && Number.isInteger(n.uploadId) && typeof n.count === "number" && typeof n.recordKey === "string" && typeof n.sourceType === "string" && (n.operation === "create" || n.operation === "correct") ? (n as ManualNotice) : null;
}

/** E2 Yükleme detayı (`/sources/uploads/:id`, spec §6, #198). Yazma yetkisi olana son versiyonda "Yeni kayıt" ve "Düzelt" (E2c). `id` null = adresteki numara sayı değil. */
export function UploadDetailScreen({ id, role }: { id: number | null; role?: Role }) {
  const [detail, setDetail] = useState<UploadDetailResponse | null>(null);
  const [error, setError] = useState("");
  const [missing, setMissing] = useState(id === null);
  const [manual, setManual] = useState<ManualTarget | null>(null);
  const opener = useRef<HTMLElement | null>(null);
  useEffect(() => { if (!manual) { opener.current?.focus(); opener.current = null; } }, [manual]); // the dialog is gone: focus goes back to the button that opened it
  const [notice] = useState(readNotice);
  const noticeRef = useRef<HTMLParagraphElement>(null);
  useEffect(() => { noticeRef.current?.focus(); }, [notice]); // ilk çizimde sonuç satırı odak alır (AccountScreen deseni)
  useEffect(() => { if (notice) window.history.replaceState(null, "", window.location.pathname + window.location.search); }, [notice]); // yenilenince satır yazmaz

  useEffect(() => {
    if (id === null) return;
    let live = true;
    getUpload(id).then((d) => { if (live) setDetail(d); }, (e) => { if (!live) return; if (e instanceof ApiFailure && e.status === 404) setMissing(true); else setError(errorText(e)); });
    return () => { live = false; };
  }, [id]);

  // API 404'ünü yalnız bu bileşen bilir; App başlık zinciri yalnız sayı olmayan id'yi kapsar.
  useEffect(() => { if (missing) document.title = `Yükleme bulunamadı · ${PRODUCT}`; }, [missing]);
  useEffect(() => { if (detail) document.title = `${versionLabel(detail.origin, detail.uploadId)} · ${PRODUCT}`; }, [detail]);

  const title = detail ? versionLabel(detail.origin, detail.uploadId) : null;
  // Konum üst çubukta (ui.md §12.3); yükleniyor: ilk öğe hemen, son öğe iskelet. Bulunamadıysa üst çubuk ekran adını gösterir.
  useLocationSlot(missing ? null : (
    <nav aria-label="Konum">
      <ol className="flex items-center gap-2">
        <li><BackLink /></li>
        <li aria-hidden="true" className="text-muted-foreground">›</li>
        <li aria-current="page">{title ?? <Skeleton className="inline-block h-4 w-24 align-middle" />}</li>
      </ol>
    </nav>
  ), [missing, title]);

  if (missing) {
    return (
      <main className="flex flex-col gap-2 p-6">
        <h1 className="text-2xl font-semibold">Yükleme bulunamadı</h1>
        <p className="text-muted-foreground">Bu numarayla bir yükleme yok.</p>
        <p><BackLink>Veri Kaynakları'na dön</BackLink></p>
      </main>
    );
  }
  const latest = detail?.versions[0];
  const isLatest = !!detail && latest?.uploadId === detail.uploadId;
  const writer = role !== undefined && canWrite(role);
  const basedOn = latest ? { uploadId: latest.uploadId, origin: latest.origin } : null;
  const openManual = (t: ManualTarget) => (el: HTMLElement) => { opener.current = el; setManual(t); };
  const actions = detail && writer && isLatest
    ? {
      onNew: (el: HTMLElement) => openManual({ sourceType: detail.sourceType, basedOn })(el),
      onEdit: (r: UploadRecordItem, el: HTMLElement) => openManual({ sourceType: detail.sourceType, basedOn, record: { recordKey: r.recordKey, fields: r.fields } })(el),
    }
    : undefined;
  const note = detail && writer && !isLatest && latest ? (
    <>Bu eski bir versiyon. Yeni kayıt ve düzeltme son versiyonda yapılır: <a href={uploadHref(latest.uploadId)} className={LINK} onClick={(e) => navigate(e, uploadHref(latest.uploadId))}>{versionLabel(latest.origin, latest.uploadId)}</a>.</>
  ) : undefined;
  return (
    <main className="flex flex-col gap-6 p-6">
      <PageHeader
        title={title ?? <Skeleton className="h-8 w-40" />}
        description={detail ? `${sourceTypeText(detail.sourceType)} · ${fileText(detail)}` : undefined}
      />
      {notice && (
        <p ref={noticeRef} tabIndex={-1} role="status" className="text-sm text-success-foreground outline-none">
          ✓ {versionLabel("manual", notice.uploadId)} kaydedildi. <span className="tabular-nums">{formatNumber(notice.count)}</span> kayıt {notice.operation === "correct" ? "düzeltildi" : "eklendi"}: <span className="font-mono">{recordIdText(notice.sourceType, notice.recordKey)}</span>.
        </p>
      )}
      {error && <ErrorAlert>{error}</ErrorAlert>}
      {!detail && !error && <><div className="grid gap-4 2xl:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]"><CardSkeleton /><TableSkeleton cols={5} /></div><TableSkeleton cols={6} /></>}
      {detail && (
        <>
          <div className="grid items-start gap-4 2xl:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]"><UploadInfo u={detail} /><Versions u={detail} /></div>
          <UploadRecords uploadId={detail.uploadId} sourceType={detail.sourceType} actions={actions} note={note} />
        </>
      )}
      {manual && <ManualEntryDialog target={manual} onClose={() => setManual(null)} />}
    </main>
  );
}
